import { listPaddocksByOrganizationQueryOptions } from "@packages/api-client/hooks/paddockController";
import type { PaddockWithCultivars } from "@packages/shared-types/grass";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDeletePaddock } from "@/api/grass";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { DashboardSkeleton } from "@/components/ui/skeleton";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { PaddockList } from "./-components/PaddockList";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/farms/$farmId/paddocks/",
)({
	// Management list (edit/delete actions); the offline paddock entry point is
	// the grass hub, which degrades to the prepared dataset.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: PaddocksPage,
});

function PaddocksPage() {
	const { orgId, farmId } = Route.useParams();
	const { t } = useTranslation();

	const { data, isLoading } = useQuery(
		listPaddocksByOrganizationQueryOptions(orgId, { establishmentId: farmId }),
	);

	const deleteMutation = useDeletePaddock();

	// Return the promise so the confirm dialog (PaddockList) can await it and
	// close only after the delete resolves.
	const handleDelete = (paddockId: string) =>
		deleteMutation.mutateAsync({
			organizationId: orgId,
			establishmentId: farmId,
			paddockId,
		});

	if (isLoading) {
		return <DashboardSkeleton />;
	}

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={t("grass.paddock.list.title")}
				showBackButton
				actions={
					<Link
						to="/app/org/$orgId/grass-production/farms/$farmId/paddocks/new"
						params={{ orgId, farmId }}
					>
						<Button>
							<Plus className="mr-2 h-4 w-4" />
							{t("grass.paddock.actions.create")}
						</Button>
					</Link>
				}
			/>
			<PaddockList
				paddocks={(data?.data ?? []) as unknown as PaddockWithCultivars[]}
				orgId={orgId}
				farmId={farmId}
				onDelete={handleDelete}
				isDeleting={deleteMutation.isPending}
				isLoading={isLoading ?? false}
			/>
		</div>
	);
}
